import { motion } from 'framer-motion';
import { SunIcon, MoonIcon, SparklesIcon } from '@heroicons/react/24/outline';

interface WelcomeHeaderProps {
  firstName: string;
  daysSober?: number;
}

function getGreeting(): { text: string; icon: typeof SunIcon } {
  const hour = new Date().getHours();
  if (hour < 12) return { text: 'Good morning', icon: SunIcon };
  if (hour < 17) return { text: 'Good afternoon', icon: SunIcon };
  return { text: 'Good evening', icon: MoonIcon };
}

function getEncouragement(daysSober?: number): string {
  const encouragements = [
    "One day at a time. You're doing great.",
    "Progress, not perfection.",
    "Every day is a new beginning.",
    "You are stronger than you know.",
    "Keep coming back. It works if you work it.",
  ];

  if (daysSober !== undefined) {
    if (daysSober === 0) return "Today is day one. You've got this.";
    if (daysSober === 1) return "One day down. Keep going!";
    if (daysSober < 7) return `${daysSober} days strong. One day at a time.`;
    if (daysSober < 30) return "You're building momentum. Keep it up!";
    if (daysSober < 90) return "Your commitment is inspiring. Stay the course.";
    return "Your journey is a testament to your strength.";
  }

  return encouragements[Math.floor(Math.random() * encouragements.length)];
}

export function WelcomeHeader({ firstName, daysSober }: WelcomeHeaderProps) {
  const greeting = getGreeting();
  const encouragement = getEncouragement(daysSober);
  const Icon = greeting.icon;

  return (
    <motion.div
      className="bg-gradient-to-br from-primary-500 to-primary-700 dark:from-primary-700 dark:to-primary-900 rounded-2xl p-6 text-white relative overflow-hidden"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

      <div className="relative">
        <div className="flex items-center gap-2 text-primary-200 mb-1">
          <Icon className="w-5 h-5" />
          <span className="text-sm font-medium">{greeting.text}</span>
        </div>

        <h1 className="font-display text-2xl sm:text-3xl font-semibold mb-2">
          {firstName}
        </h1>

        <div className="text-primary-100">
          <p className="flex items-center gap-2">
            <SparklesIcon className="w-4 h-4" />
            {encouragement}
          </p>
          {encouragement === "Keep coming back. It works if you work it." && (
            <p className="text-sm italic text-primary-200 ml-6 mt-1">
              (and it sucks like hell if you don't)
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
